/**
 * Starvie product geo-restrictions.
 *
 * Customers from EE and LT cannot view, add to cart, or purchase Starvie products.
 * Detection looks at brand, name, description, category slug/name, and SKU.
 */

import type { Product } from "@/types/product";
import type { CartItem } from "@/context/CartContext";

export const RESTRICTED_COUNTRY_CODES = ["EE", "LT"] as const;
export type RestrictedCountryCode = typeof RESTRICTED_COUNTRY_CODES[number];

export const STARVIE_BLOCK_MESSAGE_EN =
  "Starvie products are not available in your country.";
export const STARVIE_BLOCK_MESSAGE_LV =
  "Starvie produkti nav pieejami jūsu valstī.";
export const STARVIE_CHECKOUT_BLOCK_MESSAGE =
  "Starvie products cannot be purchased from Estonia or Lithuania.";

export function isRestrictedCountry(
  countryCode: string | null | undefined
): boolean {
  if (!countryCode) return false;
  return (RESTRICTED_COUNTRY_CODES as readonly string[]).includes(
    countryCode.toUpperCase()
  );
}

const STARVIE_TOKEN = "starvie";

function fieldMatches(value: unknown): boolean {
  if (!value) return false;
  return String(value).toLowerCase().includes(STARVIE_TOKEN);
}

export function isStarvieProduct(
  product:
    | (Partial<Product> & {
        brand?: string | null;
        name?: string | null;
        description?: string | null;
        category?: string | null;
        categorySlug?: string | null;
        sku?: string | null;
        specs?: Record<string, string> | null;
      })
    | null
    | undefined
): boolean {
  if (!product) return false;
  if (fieldMatches(product.brand)) return true;
  if (fieldMatches(product.name)) return true;
  if (fieldMatches(product.categorySlug)) return true;
  if (fieldMatches(product.category)) return true;
  if (fieldMatches(product.sku)) return true;
  // Metadata / specs
  const specs = product.specs;
  if (specs && typeof specs === "object") {
    for (const v of Object.values(specs)) {
      if (fieldMatches(v)) return true;
    }
  }
  // Description as last resort
  if (fieldMatches(product.description)) return true;
  return false;
}

export function cartContainsStarvieProduct(
  cart: ReadonlyArray<CartItem> | null | undefined
): boolean {
  if (!cart || cart.length === 0) return false;
  return cart.some((item) => isStarvieProduct(item.product));
}
